import React, { useCallback, useEffect, useState } from "react";
import AiFeatureToolPrep from "../../shared/Popup/AiFeatureToolPrep";
import OutpaintPreview from "./OutpaintPreview";
import OutpaintPresetCards from "./OutpaintPresetCards";
import AspectShapeIcon from "../../../shared/AspectShapeIcon";
import {
  OUTPAINT_ASPECT_PRESETS,
  getOutpaintAspectPreset,
} from "../../../../../config/tools/outpaintAspectPresets";
import {
  OUTPAINT_MAX_MARGIN,
  computeOutpaintMargins,
  hasPositiveExpand,
  outpaintComposeStageKey,
  outpaintStageExpand,
} from "../../../lib/outpaintAspectMargins";
import { outpaintSingle } from "../../../../../services/api/tools/outpaint";
import "./OutpaintToolPrep.css";

const DEFAULT_ASPECT_ID = "16-9";
/** Generate always uses webp; download format is chosen on Download Options. */
const DEFAULT_OUTPUT_FORMAT = "webp";
const ZERO_EXPAND = { left: 0, right: 0, up: 0, down: 0 };

const MARGIN_FIELDS = [
  { key: "left", label: "Left" },
  { key: "right", label: "Right" },
  { key: "up", label: "Top" },
  { key: "down", label: "Bottom" },
];

function clampMargin(value) {
  const n = parseInt(String(value), 10);
  if (Number.isNaN(n) || n < 0) return 0;
  return Math.min(OUTPAINT_MAX_MARGIN, n);
}

function measureImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    if (
      typeof url === "string" &&
      /^https?:\/\//i.test(url) &&
      !url.startsWith("blob:") &&
      !url.startsWith("data:")
    ) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () =>
      resolve({
        width: img.naturalWidth || img.width || 0,
        height: img.naturalHeight || img.height || 0,
      });
    img.onerror = () => resolve({ width: 0, height: 0 });
    img.src = url;
  });
}

function ImageSizeSync({ url, onSize }) {
  useEffect(() => {
    if (!url) {
      onSize({ width: 0, height: 0 });
      return undefined;
    }
    let cancelled = false;
    measureImage(url).then((size) => {
      if (!cancelled) onSize(size);
    });
    return () => {
      cancelled = true;
    };
  }, [url, onSize]);
  return null;
}

/**
 * AI Tools → AI Expand prep.
 * Generate calls API → show output in popup. Close always leaves (does not
 * place the result on the editor).
 */
const OutpaintToolPrep = ({
  originalImageUrl = null,
  imageFile = null,
  onDone = () => {},
  onCancel = () => {},
}) => {
  const [aspectId, setAspectId] = useState(DEFAULT_ASPECT_ID);
  const [expand, setExpand] = useState({ ...ZERO_EXPAND });
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [resultSize, setResultSize] = useState({ width: 0, height: 0 });

  const handleSourceChange = useCallback(() => {
    setExpand({ ...ZERO_EXPAND });
    setAspectId(DEFAULT_ASPECT_ID);
    setNaturalSize({ width: 0, height: 0 });
    setResultSize({ width: 0, height: 0 });
  }, []);

  useEffect(() => {
    if (!aspectId || aspectId === "custom") return;
    if (!naturalSize.width || !naturalSize.height) return;
    const preset = getOutpaintAspectPreset(aspectId);
    if (!preset) return;
    setExpand(
      computeOutpaintMargins(
        naturalSize.width,
        naturalSize.height,
        preset.targetWidth,
        preset.targetHeight
      )
    );
  }, [aspectId, naturalSize.width, naturalSize.height]);

  const selectPreset = (id, { setStageKey, hasResult } = {}) => {
    setAspectId(id);
    setStageKey?.(outpaintComposeStageKey(hasResult));
    if (!naturalSize.width || !naturalSize.height) return;
    const preset = getOutpaintAspectPreset(id);
    if (!preset) return;
    setExpand(
      computeOutpaintMargins(
        naturalSize.width,
        naturalSize.height,
        preset.targetWidth,
        preset.targetHeight
      )
    );
  };

  const setMargin = (key, raw, { setStageKey, hasResult } = {}) => {
    setAspectId("custom");
    setStageKey?.(outpaintComposeStageKey(hasResult));
    setExpand((prev) => ({ ...prev, [key]: clampMargin(raw) }));
  };

  const handleGenerate = useCallback(
    async ({ token, file, image_url }) =>
      outpaintSingle(token, file, {
        left: expand.left,
        right: expand.right,
        up: expand.up,
        down: expand.down,
        output_format: DEFAULT_OUTPUT_FORMAT,
        ...(image_url ? { image_url } : {}),
      }),
    [expand.down, expand.left, expand.right, expand.up]
  );

  const handleOpenInEditor = useCallback(
    async ({ imageUrl, creationId }) => {
      const dims = await measureImage(imageUrl);
      return onDone({
        imageUrl,
        creationId,
        width: dims.width,
        height: dims.height,
      });
    },
    [onDone]
  );

  const isGenerateReady = useCallback(
    () => hasPositiveExpand(expand),
    [expand]
  );

  return (
    <AiFeatureToolPrep
      originalImageUrl={originalImageUrl}
      imageFile={imageFile}
      onDone={handleOpenInEditor}
      onCancel={onCancel}
      onGenerate={handleGenerate}
      onSourceChange={handleSourceChange}
      generateFileName="outpaint-input.png"
      defaultStageKey="preview"
      toolKey="ai_expand"
      title="AI Expand"
      aka="AI Outpaint"
      bio="Expand the canvas; AI fills new edges so the scene continues."
      howToSteps={[
        "Upload an image",
        "Pick an aspect ratio or expand sides",
        "Tap Generate to fill the new edges",
      ]}
      howToTip="Larger expands need more credits and take longer — start with a modest crop."
      emptyPreviewLabel="Upload an image to preview expand"
      loadingLabel="Expanding…"
      emptyFooterHint="Upload an image, pick an aspect ratio, then generate."
      downloadFallbackName="expanded.png"
      isGenerateReady={isGenerateReady}
      renderSource={({
        selectedKey,
        onSelect,
        originalImageUrl: sourceUrl,
        previewImageUrl,
        onUploadClick,
        onRemove,
        uploadProgress,
        sourceName,
        sourceSizeLabel,
        actionsDisabled,
      }) => (
        <div className="otp-source">
          <ImageSizeSync url={sourceUrl} onSize={setNaturalSize} />
          <OutpaintPresetCards
            selectedKey={selectedKey}
            onSelect={onSelect}
            originalImageUrl={sourceUrl}
            previewImageUrl={previewImageUrl}
            naturalW={naturalSize.width}
            naturalH={naturalSize.height}
            expand={previewImageUrl ? ZERO_EXPAND : expand}
            onUploadClick={onUploadClick}
            onRemove={onRemove}
            uploadProgress={uploadProgress}
            sourceName={sourceName}
            sourceSizeLabel={sourceSizeLabel}
            actionsDisabled={actionsDisabled}
          />
        </div>
      )}
      renderPreview={({
        imageUrl,
        resultImageUrl,
        loading,
        emptyLabel,
        stageKey,
      }) => {
        const showingResult = Boolean(resultImageUrl && stageKey === "preview");
        const stageExpand = outpaintStageExpand({
          resultImageUrl,
          stageKey,
          expand,
        });
        const stageNaturalW =
          stageKey === "original"
            ? naturalSize.width
            : resultImageUrl
              ? resultSize.width || naturalSize.width
              : naturalSize.width;
        const stageNaturalH =
          stageKey === "original"
            ? naturalSize.height
            : resultImageUrl
              ? resultSize.height || naturalSize.height
              : naturalSize.height;
        return (
          <>
            <ImageSizeSync url={resultImageUrl} onSize={setResultSize} />
            <OutpaintPreview
              imageUrl={imageUrl}
              naturalW={stageNaturalW}
              naturalH={stageNaturalH}
              expand={stageExpand}
              loading={loading}
              resultMode={showingResult}
              emptyLabel={emptyLabel}
            />
          </>
        );
      }}
      renderControls={({
        busy,
        setStageKey,
        sourceImageUrl,
        hasResult,
      }) => (
        <>
          <div className="otp-section">
            <label className="otp-label">Aspect ratios</label>
            <div className="otp-aspect-grid" role="listbox" aria-label="Aspect ratios">
              {OUTPAINT_ASPECT_PRESETS.map((preset) => {
                const selected = aspectId === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`otp-aspect-card${selected ? " is-selected" : ""}`}
                    onClick={() =>
                      selectPreset(preset.id, { setStageKey, hasResult })
                    }
                    disabled={busy}
                  >
                    <span className="otp-aspect-card__icon" aria-hidden="true">
                      <AspectShapeIcon shape={preset.shape} size={36} />
                    </span>
                    <span className="otp-aspect-card__name">{preset.label}</span>
                    <span className="otp-aspect-card__dims">{preset.dimsLabel}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="otp-section">
            <label className="otp-label">Custom margins</label>
            <div className="otp-margins-grid">
              {MARGIN_FIELDS.map((field) => (
                <div key={field.key} className="otp-margin-row">
                  <span className="otp-margin-row__label">
                    {field.label} <span className="otp-margin-row__unit">(px)</span>
                  </span>
                  <div className="otp-margin-input">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      aria-label={`${field.label} margin in pixels`}
                      value={String(expand[field.key] ?? 0)}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^\d]/g, "");
                        setMargin(field.key, raw === "" ? 0 : raw, {
                          setStageKey,
                          hasResult,
                        });
                      }}
                      disabled={busy}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {!isGenerateReady() && sourceImageUrl && !hasResult ? (
            <p className="otp-hint">
              Add expansion on at least one side, or pick a different aspect ratio.
            </p>
          ) : null}
        </>
      )}
    />
  );
};

export default OutpaintToolPrep;
